import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, UserCheck, CalendarClock, CheckCircle2, XCircle, CalendarX, MessageSquareWarning, Zap, ZapOff } from "lucide-react";

function Stat({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default: "text-foreground",
    success: "text-[hsl(var(--success))]",
    destructive: "text-destructive",
    primary: "text-primary",
  };
  return (
    <Card className="p-6 border-border transition-transform duration-200 hover:-translate-y-1" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="uppercase text-xs tracking-[0.2em] text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${tones[tone]}`} />
      </div>
      <p className={`font-head text-4xl font-800 mt-4 tracking-tight ${tones[tone]}`}>{value}</p>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="animate-fade-up">
      <p className="uppercase text-xs tracking-[0.3em] text-muted-foreground mb-2">Overview</p>
      <h1 className="font-head text-4xl sm:text-5xl font-800 tracking-tight leading-none">Dashboard</h1>
      <p className="text-muted-foreground mt-3 mb-8">Your poultry crew at a glance.</p>

      {data && !data.twilio_enabled && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-primary/30 bg-primary/10 px-4 py-3" data-testid="simulation-banner">
          <MessageSquareWarning className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-foreground">
            <span className="font-semibold">Simulation mode.</span> Twilio isn't configured yet, so SMS sends are simulated
            (valid +61 numbers are marked delivered). Add your Twilio credentials to send real texts.
          </p>
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border border-border bg-card px-4 py-3" data-testid="automation-status">
        {data?.automation_enabled ? (
          <>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--success))]">
              <Zap className="h-4 w-4" /> Automatic sending ON
            </span>
            <span className="text-sm text-muted-foreground">
              Alerts auto-send daily at <span className="font-medium text-foreground">{data?.default_send_time}</span> (Brisbane) for the {data?.send_target === "next_day" ? "next day's" : "same day's"} shift.
              {data?.automation_last_run ? ` Last run: ${data.automation_last_run.split("@")[0]}.` : ""}
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <ZapOff className="h-4 w-4" /> Automatic sending OFF
            </span>
            <span className="text-sm text-muted-foreground">
              Manual push only. Turn on automation in <Link to="/send" className="text-primary underline-offset-2 hover:underline">Send Alerts → settings</Link>.
            </span>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={Users} label="Total staff" value={data?.staff_total ?? "–"} testid="stat-staff-total" />
        <Stat icon={UserCheck} label="Available" value={data?.staff_available ?? "–"} tone="success" testid="stat-staff-available" />
        <Stat icon={CalendarClock} label="Weekly shifts" value={data?.shifts_total ?? "–"} tone="primary" testid="stat-shifts" />
        <Stat icon={CheckCircle2} label="Delivered" value={data?.delivered ?? "–"} tone="success" testid="stat-delivered" />
        <Stat icon={XCircle} label="Undelivered" value={data?.undelivered ?? "–"} tone="destructive" testid="stat-undelivered" />
        <Stat icon={CalendarX} label="Upcoming exceptions" value={data?.exceptions_upcoming ?? "–"} testid="stat-exceptions" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-8">
        <Card className="p-6">
          <h3 className="font-head text-lg font-700 mb-1">Get started</h3>
          <p className="text-sm text-muted-foreground mb-4">Set up your roster in three steps.</p>
          <ol className="space-y-2 text-sm">
            <li>1. Add your <Link to="/staff" className="text-primary font-medium underline-offset-2 hover:underline">staff & numbers</Link></li>
            <li>2. Build the <Link to="/schedule" className="text-primary font-medium underline-offset-2 hover:underline">weekly schedule</Link> & exceptions</li>
            <li>3. <Link to="/send" className="text-primary font-medium underline-offset-2 hover:underline">Send alerts</Link> for a chosen day</li>
          </ol>
        </Card>
        <Card className="p-6">
          <h3 className="font-head text-lg font-700 mb-1">Delivery health</h3>
          <p className="text-sm text-muted-foreground mb-4">Review every day's send results.</p>
          <Link to="/summary" className="text-primary font-medium text-sm underline-offset-2 hover:underline">
            Open delivery summary →
          </Link>
        </Card>
      </div>
    </div>
  );
}
